import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

type QuantityLine = Pick<
  AssignmentLine,
  'topOption' | 'neededQuantity' | 'calculatedQuantity'
>;

/** The quantity the line is planned at now: the option's, else the need. */
export function effectiveQuantity(line: QuantityLine): number {
  return (
    line.topOption?.buyQuantity ??
    line.topOption?.transferQuantity ??
    line.neededQuantity
  );
}

/**
 * Whether the buyer can edit the line in place: a live buy or transfer plan of
 * the line's own product. A substitute plan's vendor and quantity belong to
 * the supplying product, and a make plan's to a component, so an in-place
 * override would pair them with the line's product.
 */
export function isQuantityEditable(
  line: Pick<AssignmentLine, 'status' | 'topOption'>,
): boolean {
  return (
    line.status === 'pending' &&
    line.topOption != null &&
    line.topOption.action !== 'stock' &&
    line.topOption.planKind === 'direct'
  );
}

export interface QuantityDiff {
  /** What the engine calculated; the planned quantity when it wrote none. */
  calculated: number;
  /** What the line is planned at now (the buyer's, once adjusted). */
  adjusted: number;
  differs: boolean;
}

/** Engine-calculated versus current quantity, for the Calc column. */
export function quantityDiff(line: QuantityLine): QuantityDiff {
  const adjusted = effectiveQuantity(line);
  const calculated = line.calculatedQuantity ?? adjusted;
  return { calculated, adjusted, differs: calculated !== adjusted };
}
